import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { State, ReadyState } from '../Video'
import { useVideo } from '../VideoContext'
import { FADE_DURATION_SLOW } from './constants'
import classes from './VideoOverlay.module.scss'

type VideoOverlayState = State | ReadyState | 'interacting'

const useShouldShow = (
  desiredState: VideoOverlayState | VideoOverlayState[] | undefined,
) => {
  const { readyState, state, interacting } = useVideo()

  if (!desiredState) return true

  if (Array.isArray(desiredState)) {
    if (desiredState.includes(state)) return true
    if (desiredState.includes(readyState)) return true
    if (desiredState.includes('interacting') && interacting) return true
    return false
  }

  if (desiredState === 'interacting') return interacting

  return [state, readyState].includes(desiredState)
}

/**
 * General-purpose container for any custom video overlay
 *
 * - `state`, if given, will show the contents if the conditions are met, otherwise they are always shown
 * the state will match if any of the given states match
 *
 * - `show`, if a boolean, overrides `state`
 */
export const VideoOverlay = ({
  children,
  className = '',
  style,
  state: desiredState,
  fade,
  show,
}: {
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
  state?: VideoOverlayState | VideoOverlayState[]
  fade?: boolean
  show?: boolean
}) => {
  const couldShow = useShouldShow(desiredState)
  const shouldShow = typeof show !== 'undefined' ? show : couldShow
  const { handleInteraction } = useVideo()

  const commonProps = {
    className: `position-absolute ${classes.VideoOverlay} ${className}`,
    onMouseMove: handleInteraction,
    style: { inset: 0, ...style },
    children,
  }

  if (!fade) {
    return shouldShow ? <div {...commonProps} /> : null
  }

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          {...commonProps}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ ease: 'easeOut', duration: FADE_DURATION_SLOW }}
        />
      )}
    </AnimatePresence>
  )
}
